/* eslint-disable @aix/no-console-in-backend -- environment bootstrapping module, console is the only available logger */
// Helper to safely access process.env (handles Cloudflare Workers where process.env may not exist at module load)
export const getEnvVar = (key: string): string | undefined => {
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- typeof check needed for Cloudflare Workers where `process` is a ReferenceError
  if (typeof process !== "undefined" && process.env) {
    return process.env[key]
  }
  return undefined
}

// Frontend environment detection (uses NEXT_PUBLIC_ENV - set per deployment)
export const isPreviewVercelEnv = getEnvVar("NEXT_PUBLIC_ENV") === "preview"
export const isDevelopVercelEnv = getEnvVar("NEXT_PUBLIC_ENV") === "dev"
export const isProdVercelEnv = getEnvVar("NEXT_PUBLIC_ENV") === "prod"

// =============================================================================
// AIX_ENV - Unified environment identifier for all observability (Sentry, PostHog, Axiom)
// =============================================================================

/**
 * Environment enum for AIX applications
 * Use EAixEnv.PROD instead of "prod" strings everywhere
 */
export enum EAixEnv {
  PROD = "prod",
  DEV = "dev",
  LOCAL = "local",
  TEST = "test",
}

/** @deprecated Use EAixEnv enum instead */
export type AixEnvType = `${EAixEnv}`

// @ts-expect-error - caches is a Cloudflare Workers global
export const IS_CLOUDFLARE_DEPLOYED = typeof caches !== "undefined" && typeof caches.default !== "undefined"

/**
 * Determine the current AIX environment.
 * Used for Sentry, PostHog, Axiom tagging.
 */
export const resolveAixEnv = (env: Record<string, string | undefined>): EAixEnv => {
  const explicitAixEnv = env.AIX_ENV
  if (explicitAixEnv !== undefined) {
    if (Object.values(EAixEnv).includes(explicitAixEnv as EAixEnv)) return explicitAixEnv as EAixEnv
    throw new Error(`Invalid AIX_ENV "${explicitAixEnv}". Expected one of: ${Object.values(EAixEnv).join(", ")}`)
  }

  const hasDeploymentEvidence = [env.DEPLOYED, env.RENDER, env.VERCEL, env.RAILWAY, env.CLOUDFLARE_DEPLOYED].some(
    (value) => value !== undefined && value !== "" && value !== "false" && value !== "0",
  )
  // NODE_ENV never selects an AIX environment. It is only a migration tripwire:
  // an optimized runtime without explicit AIX classification must fail closed.
  if (hasDeploymentEvidence || env.NODE_ENV === "production") {
    throw new Error("Deployed runtime is missing AIX_ENV")
  }

  if (env.VITEST === "true" || env.ENVIRONMENT === "test" || env.ENVIRONMENT === "ci") return EAixEnv.TEST

  // Legacy browser/local tooling may still expose NEXT_PUBLIC_ENV, but it is not
  // accepted as deployment authority once runtime deployment evidence exists.
  const nextPublicEnv = env.NEXT_PUBLIC_ENV
  if (nextPublicEnv === "prod") return EAixEnv.PROD
  if (nextPublicEnv === "dev" || nextPublicEnv === "preview") return EAixEnv.DEV
  if (nextPublicEnv === "local") return EAixEnv.LOCAL
  if (nextPublicEnv !== undefined) throw new Error(`Invalid NEXT_PUBLIC_ENV "${nextPublicEnv}"`)

  return EAixEnv.LOCAL
}

const determineAixEnv = (): EAixEnv => {
  const explicitAixEnv = getEnvVar("AIX_ENV")
  // Worker bindings are request-scoped and unavailable during module initialization.
  // Default that bootstrap-only phase to production-safe behavior; request contexts
  // must resolve the binding explicitly.
  if (IS_CLOUDFLARE_DEPLOYED && explicitAixEnv === undefined) return EAixEnv.PROD

  return resolveAixEnv({
    AIX_ENV: explicitAixEnv,
    NEXT_PUBLIC_ENV: getEnvVar("NEXT_PUBLIC_ENV"),
    VITEST: getEnvVar("VITEST"),
    ENVIRONMENT: getEnvVar("ENVIRONMENT"),
    NODE_ENV: getEnvVar("NODE_ENV"),
    DEPLOYED: getEnvVar("DEPLOYED"),
    RENDER: getEnvVar("RENDER"),
    VERCEL: getEnvVar("VERCEL"),
    RAILWAY: getEnvVar("RAILWAY"),
  })
}

/**
 * AIX_ENV - The canonical environment identifier
 *
 * Use this everywhere for consistent environment tagging:
 * - Sentry: scope.setTag("environment", AIX_ENV)
 * - PostHog: { environment: AIX_ENV }
 * - Axiom: { env: AIX_ENV }
 *
 * Values: EAixEnv.PROD | EAixEnv.DEV | EAixEnv.LOCAL | EAixEnv.TEST
 */
export const AIX_ENV: EAixEnv = determineAixEnv()
export const resolveIsTestEnv = (aixEnv: EAixEnv, env: Record<string, string | undefined>): boolean =>
  aixEnv === EAixEnv.TEST || env.VITEST === "true" || env.ENVIRONMENT === "test" || env.ENVIRONMENT === "ci"

// Test-runtime behavior is independent from deployment classification. For example,
// Vitest may intentionally exercise production configuration while still requiring test-safe adapters.
export const isTestEnv = resolveIsTestEnv(AIX_ENV, {
  VITEST: getEnvVar("VITEST"),
  ENVIRONMENT: getEnvVar("ENVIRONMENT"),
})

// Only log in non-Cloudflare environments where process.env is available at module load
// eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- typeof check needed for Cloudflare Workers where `process` is a ReferenceError
if (typeof process !== "undefined" && process.env && getEnvVar("NEXT_PUBLIC_ENV")) {
  console.log("[@aix/env]Environment", getEnvVar("NEXT_PUBLIC_ENV"), "AIX_ENV:", AIX_ENV)
}

export const IS_VERCEL_DEPLOYED = getEnvVar("VERCEL") === "1" || getEnvVar("NEXT_PUBLIC_VERCEL") === "1"
// Render sets RENDER=true automatically on all services
export const IS_RENDER_DEPLOYED = getEnvVar("RENDER") === "true"
export const IS_LOCAL_DEV = !getEnvVar("RENDER") && !getEnvVar("VERCEL") && !getEnvVar("RAILWAY")

// Is deployed to any environment (Vercel, Render, Cloudflare, etc.)
export const IS_ENV_DEPLOYED = IS_VERCEL_DEPLOYED || IS_RENDER_DEPLOYED || IS_CLOUDFLARE_DEPLOYED

// =============================================================================
// Deployment Validation - Fail fast if observability won't work
// =============================================================================
const isBrowser = typeof globalThis !== "undefined" && "window" in globalThis
if (!isBrowser && !IS_CLOUDFLARE_DEPLOYED && !isTestEnv) {
  // We're in a Node.js server environment - validate deployment detection
  const isAnyDeployedEnv = getEnvVar("RENDER") || getEnvVar("VERCEL") || getEnvVar("RAILWAY")
  if (isAnyDeployedEnv && !IS_ENV_DEPLOYED) {
    console.error(
      "[@aix/env] CRITICAL: Deployed environment detected but IS_ENV_DEPLOYED is false!",
      "\n  RENDER:",
      getEnvVar("RENDER"),
      "\n  VERCEL:",
      getEnvVar("VERCEL"),
      "\n  IS_ENV_DEPLOYED:",
      IS_ENV_DEPLOYED,
      "\n  This means logging (blg) and Sentry won't work properly!",
    )
  }
  // Log deployment status for debugging
  if (IS_ENV_DEPLOYED) {
    console.log(
      "[@aix/env] Deployment detected:",
      IS_RENDER_DEPLOYED ? "Render" : IS_VERCEL_DEPLOYED ? "Vercel" : IS_CLOUDFLARE_DEPLOYED ? "Cloudflare" : "Unknown",
      "| IS_ENV_DEPLOYED:",
      IS_ENV_DEPLOYED,
    )
  }
}

export const UI_HOST = getEnvVar("NEXT_PUBLIC_UI_HOST")
export const UI_HTTP_HOST = `${IS_VERCEL_DEPLOYED ? "https" : "http"}://${getEnvVar("NEXT_PUBLIC_UI_HOST")}`

export const VIDMAX_UI_HOST = `${IS_VERCEL_DEPLOYED ? "https" : "http"}://${getEnvVar("NEXT_PUBLIC_VIDMAX_UI_HOST")}`
export const OS_UI_HOST =
  getEnvVar("OS_UI_HOST") ||
  `${IS_VERCEL_DEPLOYED ? "https" : "http"}://${getEnvVar("NEXT_PUBLIC_OS_UI_HOST") || "localhost:3015"}`

export const VOICE_ASSET_S3_BUCKET_URL = "https://ghost-voice-assets.s3.us-west-2.amazonaws.com"

// Langfuse configuration (loaded from Infisical)
export const LANGFUSE_SECRET_KEY = getEnvVar("LANGFUSE_SECRET_KEY")
export const LANGFUSE_PUBLIC_KEY = getEnvVar("LANGFUSE_PUBLIC_KEY")
export const LANGFUSE_BASE_URL = "https://us.cloud.langfuse.com" // Hardcoded
export const LANGFUSE_PROJECT_URL = getEnvVar("NEXT_PUBLIC_LANGFUSE_PROJECT_URL") || getEnvVar("LANGFUSE_PROJECT_URL")
export const AXIOM_API_TOKEN = getEnvVar("AXIOM_API_TOKEN")
export const SENTRY_AUTH_TOKEN = getEnvVar("SENTRY_AUTH_TOKEN")
export const EXPO_TOKEN = getEnvVar("EXPO_TOKEN")

// Validation: Check if Langfuse keys are present (only in server/backend context)
// Skip warning in browser (client-side) and Cloudflare Workers (env loaded at request time)
if (!isBrowser && !IS_CLOUDFLARE_DEPLOYED && (!LANGFUSE_SECRET_KEY || !LANGFUSE_PUBLIC_KEY)) {
  console.warn("[@aix/env] LANGFUSE_SECRET_KEY or LANGFUSE_PUBLIC_KEY not found in environment variables")
}

// =============================================================================
// Secret Fingerprints - Log last 6 chars of secrets at startup for debugging
// =============================================================================

const SECRET_ENV_KEYS = [
  "SENTRY_DSN",
  "OPENAI_API_KEY",
  "ANTHROPIC_API_KEY",
  "CLAUDE_API_KEY",
  "GEMINI_API_KEY",
  "GROQ_API_KEY",
  "REPLICATE_API_TOKEN",
  "FAL_AI_API_KEY",
  "OPENROUTER_API_KEY",
  "RESEND_API_KEY",
  "STRIPE_SECRET_KEY",
  "STRIPE_VIDMAX_SECRET_KEY",
  "STRIPE_PEBBLE_SECRET_KEY",
  "STRIPE_LUNCHBREAK_SECRET_KEY",
  "UPSTASH_REDIS_REST_TOKEN",
  "SLACK_BOT_TOKEN",
  "FB_CAPI_ACCESS_TOKEN",
  "TIKTOK_CAPI_ACCESS_TOKEN",
  "LANGFUSE_SECRET_KEY",
  "LANGFUSE_PUBLIC_KEY",
  "POSTHOG_API_KEY",
  "LOOPS_API_KEY",
  "INFISICAL_TOKEN",
] as const

const MIN_LENGTH_FOR_FINGERPRINT = 20

const getFingerprint = (value: string): string => {
  if (value.length < MIN_LENGTH_FOR_FINGERPRINT) return "***"
  return `...${value.slice(-6)}`
}

/**
 * Get the last 6 characters of each configured secret for debugging.
 * Only shows suffix for secrets 20+ chars (shorter ones show "***" to avoid exposure).
 * Returns a record of { secretName: fingerprint } for structured logging.
 */
export const getSecretFingerprints = (): Record<string, string> => {
  const result: Record<string, string> = {}
  for (const key of SECRET_ENV_KEYS) {
    const value = getEnvVar(key)
    result[key] = value ? getFingerprint(value) : "(not set)"
  }
  return result
}
